import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   PostnatalCareStyles — Shared styled components for
   Postnatal Care (Maternal + Newborn), Tasks 15 & 16.
   Theme: Warm teal (#00695c) — nurturing postpartum feel
   ═══════════════════════════════════════════════════════════════ */

const PRIMARY = "#00695c";
const PRIMARY_LIGHT = "#00897b";
const PRIMARY_BG = "#e0f2f1";
const SECONDARY = "#283593";
const SUCCESS = "#2e7d32";
const WARNING = "#f57f17";
const DANGER = "#c62828";
const INFO = "#0277bd";
const PINK = "#ad1457";
const BABY_BLUE = "#0288d1";

/* ─── Page Wrapper ─── */
export const PNCPageWrapper = styled("div")({
    padding: "16px 20px 32px",
    background: "#f5f6fa",
    minHeight: "calc(100vh - 120px)",
    "@media(max-width: 768px)": { padding: "10px 8px 24px" },
});

/* ─── Header ─── */
export const PNCHeader = styled("div")(function (props) {
    const bg = props.variant === "newborn"
        ? "linear-gradient(135deg, " + BABY_BLUE + " 0%, #039be5 100%)"
        : "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)";
    return {
        background: bg,
        borderRadius: "12px",
        padding: "20px 24px",
        marginBottom: "16px",
        color: "#fff",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
        "@media(max-width: 768px)": { padding: "14px 16px", flexDirection: "column", alignItems: "flex-start" },
    };
});

export const PNCHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "4px" });
export const PNCHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#fff" });
export const PNCHeaderSub = styled("p")({ margin: 0, fontSize: "0.85rem", opacity: 0.85 });

export const PNCHeaderStats = styled("div")({ display: "flex", gap: "10px", flexWrap: "wrap" });

export const PNCStatChip = styled("div")(function (props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    if (props.variant === "info") { bg = "rgba(2,119,189,0.3)"; border = "rgba(2,119,189,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

/* ─── Main Tab Bar ─── */
export const PNCTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const PNCTabBtn = styled("button")(function (props) {
    const activeBg = props.color || PRIMARY;
    return {
        flex: "1 1 auto",
        padding: "10px 18px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.active ? 700 : 500,
        background: props.active ? activeBg : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? activeBg : PRIMARY_BG },
    };
});

export const PNCTabBadge = styled("span")(function (props) {
    return {
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 700,
        background: props.active ? "rgba(255,255,255,0.25)" : "#eee",
        color: props.active ? "#fff" : "#888",
    };
});

/* ─── Alert Cards ─── */
export const PNCAlertContainer = styled("div")({
    display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px",
});

export const PNCAlertCard = styled("div")(function (props) {
    const colors = { critical: DANGER, warning: WARNING, info: INFO };
    const c = colors[props.severity] || INFO;
    return {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "10px",
        background: c + "0a",
        border: "1px solid " + c + "30",
        borderLeft: "4px solid " + c,
        animation: "slideInPNC 0.3s ease-out",
        "@keyframes slideInPNC": {
            from: { opacity: 0, transform: "translateX(-10px)" },
            to: { opacity: 1, transform: "translateX(0)" },
        },
    };
});

export const PNCAlertIcon = styled("span")({ fontSize: "1.3rem", lineHeight: 1.3 });
export const PNCAlertContent = styled("div")({ flex: 1, minWidth: 0 });
export const PNCAlertTitle = styled("div")({ fontWeight: 700, fontSize: "0.85rem", color: "#333", marginBottom: "2px" });
export const PNCAlertDetail = styled("div")({ fontSize: "0.8rem", color: "#555" });
export const PNCAlertAction = styled("div")({ fontSize: "0.75rem", color: "#777", marginTop: "4px", fontStyle: "italic" });

/* ─── Form Card ─── */
export const PNCFormCard = styled("div")(function (props) {
    return {
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        marginBottom: "14px",
        borderTop: "3px solid " + (props.color || PRIMARY),
        overflow: "hidden",
    };
});

export const PNCFormHeader = styled("div")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 18px",
        cursor: props.collapsible ? "pointer" : "default",
        background: props.collapsed ? "#fafafa" : "#fff",
        borderBottom: props.collapsed ? "none" : "1px solid #f0f0f0",
        transition: "background 0.2s",
        ":hover": props.collapsible ? { background: "#f5f7ff" } : {},
    };
});

export const PNCFormHeaderLeft = styled("div")({ display: "flex", alignItems: "center", gap: "10px" });
export const PNCFormIcon = styled("span")({ fontSize: "1.15rem" });
export const PNCFormTitle = styled("h3")({ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: "#333" });
export const PNCFormBadge = styled("span")(function (props) {
    return {
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background: props.variant === "success" ? "#e8f5e9" : props.variant === "warning" ? "#fff3e0" : props.variant === "danger" ? "#fce4ec" : "#e8eaf6",
        color: props.variant === "success" ? SUCCESS : props.variant === "warning" ? WARNING : props.variant === "danger" ? DANGER : PRIMARY,
    };
});

export const PNCFormBody = styled("div")(function (props) {
    return {
        padding: props.collapsed ? "0" : "16px 18px",
        maxHeight: props.collapsed ? "0" : "3000px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        opacity: props.collapsed ? 0 : 1,
    };
});

/* ─── Form Grid ─── */
export const PNCFormGrid = styled("div")(function (props) {
    const cols = props.cols || 4;
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + cols + ", 1fr)",
        gap: "14px",
        "@media(max-width: 1024px)": { gridTemplateColumns: "repeat(3, 1fr)" },
        "@media(max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
        "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
    };
});

export const PNCFormGroup = styled("div")(function (props) {
    return {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        gridColumn: props.span ? "span " + props.span : "auto",
        "@media(max-width: 768px)": { gridColumn: props.span > 2 ? "span 2" : "auto" },
        "@media(max-width: 480px)": { gridColumn: "span 1" },
    };
});

export const PNCLabel = styled("label")(function (props) {
    return {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "#555",
        ":after": props.required ? { content: "' *'", color: DANGER } : {},
    };
});

export const PNCHint = styled("span")(function (props) {
    return {
        fontSize: "0.72rem",
        color: props.warn ? DANGER : "#888",
        fontWeight: props.warn ? 600 : 400,
    };
});

/* ─── Shared Input ─── */
export const PNCInput = styled("input")({
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
    outline: "none",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

export const PNCSelect = styled("select")({
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    ":focus": { borderColor: PRIMARY },
});

export const PNCTextarea = styled("textarea")({
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
    outline: "none",
    resize: "vertical",
    minHeight: "70px",
    fontFamily: "inherit",
    ":focus": { borderColor: PRIMARY },
});

/* ─── Checkbox / Toggle ─── */
export const PNCCheckboxGrid = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + (props.cols || 3) + ", 1fr)",
        gap: "8px",
        "@media(max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
        "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
    };
});

export const PNCCheckboxItem = styled("label")(function (props) {
    const borderCol = props.checked ? (props.danger ? DANGER : PRIMARY) : "#e0e0e0";
    const bgCol = props.checked ? (props.danger ? "#fce4ec" : PRIMARY_BG) : "#fff";
    const textCol = props.checked ? (props.danger ? DANGER : PRIMARY) : "#555";
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        borderRadius: "8px",
        border: "1px solid " + borderCol,
        background: bgCol,
        cursor: "pointer",
        fontSize: "0.8rem",
        color: textCol,
        fontWeight: props.checked ? 600 : 400,
        transition: "all 0.15s",
        ":hover": { borderColor: props.danger ? DANGER : PRIMARY, background: props.danger ? "#fce4ec" : PRIMARY_BG },
    };
});

export const PNCToggle = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 14px",
        borderRadius: "8px",
        background: props.checked ? PRIMARY_BG : "#fafafa",
        border: "1px solid " + (props.checked ? PRIMARY : "#eee"),
        cursor: "pointer",
        transition: "all 0.15s",
        ":hover": { background: PRIMARY_BG },
    };
});

export const PNCToggleSwitch = styled("span")(function (props) {
    return {
        width: "36px",
        height: "20px",
        borderRadius: "10px",
        background: props.checked ? PRIMARY : "#ccc",
        position: "relative",
        transition: "background 0.2s",
        ":after": {
            content: '""',
            position: "absolute",
            top: "2px",
            left: props.checked ? "18px" : "2px",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        },
    };
});

export const PNCToggleLabel = styled("span")(function (props) {
    return {
        fontSize: "0.84rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? "#333" : "#666",
    };
});

/* ─── Schedule Tracker ─── */
export const PNCScheduleCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "14px",
    overflow: "hidden",
});

export const PNCScheduleHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    color: "#fff",
});

export const PNCScheduleTitle = styled("h3")({
    margin: 0, fontSize: "0.95rem", fontWeight: 700, color: "#fff",
});

export const PNCScheduleProgress = styled("div")({
    fontSize: "0.8rem", fontWeight: 600, opacity: 0.9,
});

export const PNCScheduleBody = styled("div")({ padding: "16px 18px" });

export const PNCScheduleGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    "@media(max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
});

export const PNCScheduleItem = styled("div")(function (props) {
    const borderColors = {
        attended: SUCCESS,
        due: WARNING,
        missed: DANGER,
        upcoming: "#90a4ae",
        future: "#e0e0e0",
    };
    const bgColors = {
        attended: "#e8f5e9",
        due: "#fff8e1",
        missed: "#fce4ec",
        upcoming: "#f5f5f5",
        future: "#fafafa",
    };
    const c = borderColors[props.status] || "#e0e0e0";
    const bg = bgColors[props.status] || "#fafafa";
    return {
        padding: "14px 16px",
        borderRadius: "10px",
        border: "2px solid " + c,
        background: bg,
        textAlign: "center",
        cursor: props.status === "attended" ? "pointer" : "default",
        transition: "all 0.2s",
        ":hover": props.status === "attended" ? { transform: "translateY(-2px)", boxShadow: "0 3px 8px rgba(0,0,0,0.1)" } : {},
    };
});

export const PNCScheduleVisitNum = styled("div")(function (props) {
    const statusColors = {
        attended: SUCCESS,
        due: WARNING,
        missed: DANGER,
        upcoming: "#607d8b",
        future: "#bdbdbd",
    };
    return {
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        background: statusColors[props.status] || "#bdbdbd",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 8px",
        fontSize: "0.85rem",
        fontWeight: 700,
    };
});

export const PNCScheduleLabel = styled("div")({
    fontSize: "0.78rem", fontWeight: 600, color: "#333", marginBottom: "4px",
});

export const PNCScheduleDate = styled("div")({
    fontSize: "0.72rem", color: "#888",
});

export const PNCScheduleStatus = styled("span")(function (props) {
    const statusColors = {
        attended: SUCCESS,
        due: WARNING,
        missed: DANGER,
        upcoming: "#607d8b",
        future: "#bdbdbd",
    };
    return {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.68rem",
        fontWeight: 700,
        textTransform: "uppercase",
        marginTop: "6px",
        background: (statusColors[props.status] || "#bdbdbd") + "18",
        color: statusColors[props.status] || "#bdbdbd",
    };
});

/* ─── EPDS Card ─── */
export const EPDSCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    borderTop: "3px solid " + PINK,
    marginBottom: "14px",
    overflow: "hidden",
});

export const EPDSHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: "1px solid #f0f0f0",
});

export const EPDSTitle = styled("h3")({ margin: 0, fontSize: "0.95rem", fontWeight: 700, color: "#333" });

export const EPDSScore = styled("div")(function (props) {
    let c = SUCCESS;
    if (props.score >= 13) c = DANGER;
    else if (props.score >= 10) c = WARNING;
    return {
        padding: "4px 14px",
        borderRadius: "16px",
        fontSize: "0.85rem",
        fontWeight: 700,
        background: c + "18",
        color: c,
    };
});

export const EPDSBody = styled("div")({ padding: "16px 18px" });

export const EPDSQuestion = styled("div")(function (props) {
    return {
        marginBottom: "16px",
        padding: "12px 16px",
        borderRadius: "8px",
        background: props.answered ? "#f8faf8" : "#fff",
        border: "1px solid " + (props.critical ? DANGER + "40" : "#eee"),
    };
});

export const EPDSQuestionText = styled("div")(function (props) {
    return {
        fontSize: "0.88rem",
        fontWeight: 600,
        color: props.critical ? DANGER : "#333",
        marginBottom: "10px",
    };
});

export const EPDSOptionList = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
});

export const EPDSOption = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 12px",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "0.82rem",
        background: props.selected ? PRIMARY_BG : "transparent",
        border: "1px solid " + (props.selected ? PRIMARY : "transparent"),
        color: props.selected ? PRIMARY : "#555",
        fontWeight: props.selected ? 600 : 400,
        transition: "all 0.15s",
        ":hover": { background: PRIMARY_BG },
    };
});

export const EPDSRadio = styled("span")(function (props) {
    return {
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        border: "2px solid " + (props.selected ? PRIMARY : "#ccc"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ":after": props.selected ? {
            content: '""',
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: PRIMARY,
        } : {},
    };
});

export const EPDSResult = styled("div")(function (props) {
    let c = SUCCESS;
    if (props.level === "high") c = DANGER;
    else if (props.level === "moderate") c = WARNING;
    return {
        padding: "16px",
        borderRadius: "10px",
        background: c + "0d",
        border: "1px solid " + c + "30",
        marginTop: "12px",
    };
});

export const EPDSResultTitle = styled("div")(function (props) {
    let c = SUCCESS;
    if (props.level === "high") c = DANGER;
    else if (props.level === "moderate") c = WARNING;
    return {
        fontSize: "0.95rem",
        fontWeight: 700,
        color: c,
        marginBottom: "4px",
    };
});

export const EPDSResultDetail = styled("div")({
    fontSize: "0.82rem", color: "#555",
});

/* ─── Visit History / Timeline ─── */
export const PNCVisitTimeline = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "14px",
});

export const PNCVisitCard = styled("div")(function (props) {
    const borderCol = props.active ? PRIMARY : "#e0e0e0";
    return {
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid " + borderCol,
        borderLeft: "4px solid " + (props.variant === "newborn" ? BABY_BLUE : PRIMARY),
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        ":hover": { transform: "translateY(-1px)", boxShadow: "0 3px 8px rgba(0,0,0,0.08)" },
    };
});

export const PNCVisitCardHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #f5f5f5",
});

export const PNCVisitCardBody = styled("div")({
    padding: "12px 16px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "8px",
});

export const PNCVisitCardField = styled("div")({});
export const PNCVisitCardLabel = styled("div")({ fontSize: "0.7rem", color: "#999", fontWeight: 500 });
export const PNCVisitCardValue = styled("div")(function (props) {
    return {
        fontSize: "0.82rem",
        fontWeight: 600,
        color: props.warn ? DANGER : "#333",
    };
});

/* ─── Summary Strip ─── */
export const PNCSummaryStrip = styled("div")({
    display: "flex",
    gap: "12px",
    padding: "12px 16px",
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "16px",
    overflowX: "auto",
    "@media(max-width: 768px)": { gap: "8px" },
});

export const PNCSummaryItem = styled("div")(function (props) {
    return {
        flex: "1 0 auto",
        textAlign: "center",
        padding: "6px 12px",
        borderRadius: "8px",
        background: props.warn ? DANGER + "0d" : "#f8f9fa",
        minWidth: "80px",
    };
});

export const PNCSummaryLabel = styled("div")({ fontSize: "0.7rem", color: "#888", fontWeight: 500, marginBottom: "2px" });
export const PNCSummaryValue = styled("div")(function (props) {
    return {
        fontSize: "0.9rem",
        fontWeight: 700,
        color: props.warn ? DANGER : "#333",
    };
});

/* ─── Weight Trend ─── */
export const WeightTrendCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    borderTop: "3px solid " + BABY_BLUE,
    marginBottom: "14px",
    overflow: "hidden",
});

export const WeightTrendHeader = styled("div")({
    padding: "12px 18px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
});

export const WeightTrendBody = styled("div")({ padding: "16px 18px" });

export const WeightBar = styled("div")(function (props) {
    const maxW = props.maxWeight || 4000;
    const pct = Math.min((props.weight / maxW) * 100, 100);
    const c = props.warning ? WARNING : props.danger ? DANGER : BABY_BLUE;
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "8px",
    };
});

export const WeightBarLabel = styled("div")({
    fontSize: "0.78rem", fontWeight: 600, color: "#555", minWidth: "60px",
});

export const WeightBarTrack = styled("div")({
    flex: 1,
    height: "20px",
    borderRadius: "10px",
    background: "#f0f0f0",
    overflow: "hidden",
    position: "relative",
});

export const WeightBarFill = styled("div")(function (props) {
    let c = BABY_BLUE;
    if (props.warning) c = WARNING;
    if (props.danger) c = DANGER;
    return {
        height: "100%",
        borderRadius: "10px",
        background: "linear-gradient(90deg, " + c + " 0%, " + c + "88 100%)",
        width: (props.percent || 0) + "%",
        transition: "width 0.5s ease",
    };
});

export const WeightBarValue = styled("div")(function (props) {
    return {
        fontSize: "0.8rem",
        fontWeight: 700,
        color: props.danger ? DANGER : props.warning ? WARNING : "#333",
        minWidth: "55px",
        textAlign: "right",
    };
});

/* ─── Buttons ─── */
export const PNCPrimaryBtn = styled("button")(function (props) {
    const bg = props.variant === "newborn" ? BABY_BLUE : PRIMARY;
    const bgHover = props.variant === "newborn" ? "#0277bd" : PRIMARY_LIGHT;
    return {
        padding: "10px 24px",
        borderRadius: "8px",
        border: "none",
        background: props.disabled ? "#bbb" : bg,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s",
        ":hover": props.disabled ? {} : { background: bgHover, transform: "translateY(-1px)" },
    };
});

export const PNCSecondaryBtn = styled("button")(function (props) {
    const c = props.variant === "newborn" ? BABY_BLUE : PRIMARY;
    return {
        padding: "10px 24px",
        borderRadius: "8px",
        border: "1px solid " + c,
        background: "#fff",
        color: c,
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: "pointer",
        transition: "all 0.2s",
        ":hover": { background: PRIMARY_BG },
    };
});

export const PNCDangerBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    ":hover": { background: "#b71c1c" },
});

export const PNCButtonRow = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    marginTop: "16px",
    flexWrap: "wrap",
});

/* ─── Loading / Empty ─── */
export const PNCLoading = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    color: "#888",
    fontSize: "0.9rem",
});

export const PNCEmptyState = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
});

export const PNCEmptyIcon = styled("div")({
    fontSize: "3rem",
    marginBottom: "16px",
    opacity: 0.6,
});

export const PNCEmptyTitle = styled("h3")({
    margin: "0 0 8px",
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#444",
});

export const PNCEmptyDesc = styled("p")({
    margin: "0 0 20px",
    fontSize: "0.85rem",
    color: "#888",
    maxWidth: "400px",
    marginLeft: "auto",
    marginRight: "auto",
});

/* ─── Section Divider ─── */
export const PNCSectionDivider = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: "8px 0 12px",
});

export const PNCSectionLine = styled("div")({
    flex: 1,
    height: "1px",
    background: "#e0e0e0",
});

export const PNCSectionLabel = styled("span")({
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    color: "#999",
});

/* ─── Inline Stat ─── */
export const PNCInlineStat = styled("div")({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 10px",
    borderRadius: "12px",
    background: "#f5f5f5",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#555",
});
